import React, { useState, useEffect, useMemo } from 'react';
import { Extra } from '../../models/types';
import {
    calculateExtraTotal,
    getDefaultExtraAmount,
    getSelectedExtraAmount,
} from '../../utils/extraPricing';
import { formatExtraDisplayName } from '../../utils/extraDisplayName';
import './ExtraInfoModal.css';

interface ExtraInfoModalProps {
    extra: Extra;
    isOpen: boolean;
    selected: boolean;
    onClose: () => void;
    onConfirm: (extra: Extra) => void;
    onRemove: (extra: Extra) => void;
}

const currencyFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
});

const ExtraInfoModal: React.FC<ExtraInfoModalProps> = ({
    extra,
    selected,
    isOpen,
    onClose,
    onConfirm,
    onRemove,
}) => {
    const [amount, setAmount] = useState<number>(() => getDefaultExtraAmount(extra));

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setAmount(selected ? getSelectedExtraAmount(extra) : getDefaultExtraAmount(extra));
    }, [isOpen, extra, selected]);

    const minAmount = getDefaultExtraAmount(extra);

    const previewExtra = useMemo(
        () => ({ ...extra, extra_amount: amount }),
        [extra, amount]
    );

    const formattedPrice = useMemo(
        () => currencyFormatter.format(calculateExtraTotal(previewExtra)),
        [previewExtra]
    );

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const updatedExtra = {
            ...extra,
            extra_amount: Number(amount < minAmount ? minAmount : amount),
        };
        onConfirm(updatedExtra);
        onClose();
    };

    const handleRemove = () => {
        onRemove(extra);
        onClose();
    };

    const decreaseAmount = () => {
        setAmount((current) => Math.max(minAmount, Number(current) - 1));
    };

    const increaseAmount = () => {
        setAmount((current) => Number(current) + 1);
    };

    const renderQuantityStepper = () => {
        if (extra.info_type !== 'dialog_int_amount') {
            return null;
        }

        return (
            <div className="form-group quantity-stepper-group">
                <label htmlFor="amount">Cantidad</label>
                <div className="quantity-stepper" role="group" aria-label="Seleccionar cantidad">
                    <button
                        type="button"
                        id="amount-decrease"
                        className="quantity-stepper-btn"
                        onClick={decreaseAmount}
                        disabled={amount <= minAmount}
                        aria-label="Quitar una unidad"
                    >
                        −
                    </button>
                    <output id="amount" className="quantity-stepper-value" aria-live="polite">
                        {amount}
                    </output>
                    <button
                        type="button"
                        id="amount-increase"
                        className="quantity-stepper-btn"
                        onClick={increaseAmount}
                        aria-label="Añadir una unidad"
                    >
                        +
                    </button>
                </div>
                <p className="quantity-stepper-price">{formattedPrice}</p>
                {minAmount > 1 && (
                    <p className="quantity-stepper-hint">Mínimo {minAmount} unidades</p>
                )}
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="extra-modal-title"
            >
                <h2 id="extra-modal-title">{formatExtraDisplayName(extra)}</h2>
                <form onSubmit={handleSubmit}>
                    {renderQuantityStepper()}
                    <div className="modal-buttons">
                        {selected && (
                            <button type="button" onClick={handleRemove} className="remove-button">
                                Eliminar
                            </button>
                        )}
                        <button type="button" onClick={onClose} className="cancel-button">
                            Cancelar
                        </button>
                        <button type="submit" className="confirm-button">
                            {selected ? 'Guardar' : 'Añadir'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExtraInfoModal;
